import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BookOpen, UploadSimple, DownloadSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const CATEGORIES = ["Travel","Leave","Salary Deductions","Other"];

export default function Policies() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes("admin");
  const [policies, setPolicies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title:"", category:"Travel", description:"" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/hr/policies");
    setPolicies(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (!file) { toast.error("Pick a PDF"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/hr/policies?title=${encodeURIComponent(form.title)}&category=${encodeURIComponent(form.category)}&description=${encodeURIComponent(form.description)}`, fd, { headers:{ "Content-Type":"multipart/form-data" } });
      toast.success("Policy uploaded");
      setOpen(false); setForm({ title:"", category:"Travel", description:"" }); setFile(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    await api.delete(`/hr/policies/${id}`);
    setPolicies((curr) => curr.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const grouped = policies.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});

  return (
    <Layout title="Company Policies" subtitle="View-only · download PDFs · admin manages"
      actions={isAdmin ? <button onClick={()=>setOpen(true)} data-testid="upload-policy-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]"><UploadSimple size={16} weight="bold"/> Upload Policy</button> : null}>
      <div className="mb-5 flex items-center gap-2 text-[11px] font-mono-shelv tracking-widest uppercase text-[#717680]">
        <ShieldCheck size={14} weight="bold" className="text-[#0D9488]"/> Read-only · download &amp; review · only admin can upload or replace
      </div>
      {policies.length === 0 ? (
        <div className="border border-dashed border-black/15 bg-white px-4 py-16 text-center text-[#717680]" data-testid="policies-empty">
          <BookOpen size={32} weight="duotone" className="mx-auto text-[#D90429]/40 mb-2"/>
          No policies uploaded yet{isAdmin?" — click 'Upload Policy' to add Travel, Leave & Salary Deduction policies.":""}.
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="overline mb-3">{cat} <span className="text-[#717680]">· {list.length}</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map(p => (
                  <div key={p.id} className="border border-black/10 bg-white p-4 hover:border-[#D90429] hover:shadow-[0_20px_40px_-20px_rgba(217,4,41,0.25)] transition-all" data-testid={`policy-card-${p.id}`}>
                    <BookOpen size={22} weight="duotone" className="text-[#D90429] mb-2"/>
                    <div className="font-display text-base font-bold text-[#0A0A0A]">{p.title}</div>
                    <div className="text-[10px] text-[#717680] font-mono-shelv tracking-widest mt-0.5">{p.file_filename}</div>
                    {p.description && <div className="text-xs text-[#4A4D57] mt-2 line-clamp-2">{p.description}</div>}
                    <div className="flex items-center gap-1 mt-3 pt-3 border-t border-black/5">
                      <a href={`${API}/files/public/${p.file_path}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-[#0A0A0A] text-white text-[11px] font-bold hover:bg-[#D90429]" data-testid={`download-policy-${p.id}`}>
                        <DownloadSimple size={12} weight="bold"/> Download
                      </a>
                      {isAdmin && <button onClick={()=>remove(p.id)} className="p-1.5 text-[#D90429] hover:bg-[#D90429]/10" data-testid={`del-policy-${p.id}`}><Trash size={12}/></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-none">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Upload policy</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="e.g., Travel Reimbursement Policy 2026" className="rounded-none" data-testid="policy-title-input"/></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}>
                <SelectTrigger className="rounded-none" data-testid="policy-category-select"><SelectValue/></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Short description</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="rounded-none"/></div>
            <div>
              <Label>PDF *</Label>
              <input ref={fileRef} type="file" accept=".pdf" onChange={e=>setFile(e.target.files?.[0]||null)} className="block w-full text-sm" data-testid="policy-file-input"/>
              {file && <div className="text-[10px] text-[#717680] mt-1">{file.name} · {(file.size/1024).toFixed(1)} KB</div>}
            </div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={upload} disabled={uploading} data-testid="upload-policy-submit" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-50">{uploading?"Uploading…":"Upload"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
